/**
 * ============================================================================
 * LIVING FAMILY NETWORK — FamilyUnit Engine (overlay layer)
 * ----------------------------------------------------------------------------
 * This module is the ARCHITECTURAL HOME of every spouse / partner / fiancé /
 * ex-spouse / step-parent / co-parent / in-law / blended-household relationship
 * in the KinsCircle graph. It is intentionally separate from the bloodline
 * engine (`src/lib/familyPlacement.ts`, `src/contexts/FamilyGraphContext`'s
 * kinship walk, `pyramidLayout.ts` Step 6+ parent-edge math) so the two
 * systems can evolve independently:
 *
 *   ┌──────────────────────────────────────────────────────────────────────┐
 *   │  BLOODLINE ENGINE                                                    │
 *   │    parent_ids → tier index, sibling lineage, ancestor / descendant   │
 *   │    walks, kinship code (cousin / pibling / nibling / ancestor / …)   │
 *   │    Strict, mathematically deterministic. NEVER reads spouse_id to    │
 *   │    decide tiers.                                                     │
 *   └──────────────────────────────────────────────────────────────────────┘
 *                                  ▲
 *                                  │  same person ids, no shared mutation
 *                                  ▼
 *   ┌──────────────────────────────────────────────────────────────────────┐
 *   │  FAMILY-UNIT ENGINE  (this file)                                     │
 *   │    spouse_id  +  spouse_partner_edges  →  FamilyUnit overlays        │
 *   │    married, partner, fiancé, co-parent, divorced, ex-spouse,         │
 *   │    deceased-spouse, step-parent, son-/daughter-/in-law variants,     │
 *   │    same-sex couples, multiple marriages over time, blended           │
 *   │    households.                                                       │
 *   │    Attaches to ALREADY-VISIBLE bloodline anchors. NEVER requires     │
 *   │    ancestry proof for placement — if ONE partner is on the tree,     │
 *   │    the other can render beside them.                                 │
 *   └──────────────────────────────────────────────────────────────────────┘
 *
 * RULES THIS MODULE OBEYS:
 *   1. PURE / READ-ONLY. No DB writes. No member mutation. No spouse_id
 *      writes. No parent_ids writes. The repair flows already in
 *      `FamilyGraphContext.repairSpouseEdge` and `addRelativeUniversal`
 *      remain the single source of truth for actually persisting partner
 *      links — this module ONLY synthesizes the in-memory overlay layer
 *      that the renderer + Needs-connection gate consult.
 *   2. DOES NOT REPLACE BLOODLINE LOGIC. parent_ids / kinship / generation
 *      tiers / sibling math all stay in `familyPlacement.ts` +
 *      `pyramidLayout.ts`. Calling code that asks "is this person provably
 *      a cousin of YOU?" still goes through the bloodline engine.
 *   3. SPOUSE-VISIBILITY PROOF IS INDEPENDENT FROM ANCESTRY PROOF.
 *      `isPartnerOfAnyVisibleMember` returns true for ANY person who has a
 *      spouse_id link OR a spouse_partner_edges row OR a reverse-direction
 *      spouse pointer to a member who is itself placeable on the tree.
 *      Bloodline proof is never required.
 *   4. NEVER CREATES LINEAGE RECURSION LOOPS. Walks are cycle-guarded so
 *      a spouse-of-a-spouse-of-a-spouse chain (rare, but legal in blended
 *      families with multiple marriages) cannot hang the renderer.
 *   5. FUTURE-PROOF. The exported types (FamilyUnit, Household,
 *      RelationshipHistoryEntry) match the additive Supabase schema added
 *      in the May 2026 pivot migration:
 *          public.households
 *          public.family_units
 *          public.relationship_history
 *          public.spouse_partner_edges (extended with unit_type/started_at/
 *                                       ended_at/is_current/metadata)
 *      Loaders below read those tables; if a table is absent in the
 *      current environment (production has not migrated yet), the loader
 *      returns an empty list and the consumer falls back to spouse_id
 *      semantics. NOTHING crashes.
 *
 * USAGE:
 *   • `loadSpousePartnerEdges(supabase, ownerUserId)` — fetch every
 *     edge involving the given owner's graph, normalized to a stable
 *     SpousePartnerEdge[] shape.
 *   • `enumeratePartnersOf(memberId, members, edges)` — every member
 *     that the given person is partnered with via ANY surface (spouse_id
 *     forward / reverse / spouse_partner_edges row). De-duplicated.
 *   • `isPartnerOfAnyVisibleMember(memberId, members, edges, isVisible)` —
 *     the placement-anchor primitive used by `isPlacementProvable` so a
 *     son-in-law / daughter-in-law / partner-of-an-ancestor renders even
 *     when their own ancestry is unreachable.
 *   • `buildFamilyUnits(members, edges)` — synthesizes one FamilyUnit per
 *     unique partner pair, preferring spouse_partner_edges metadata when
 *     present (unit_type / started_at / ended_at / is_current) and falling
 *     back to a default 'married' unit when only spouse_id exists.
 * ============================================================================
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { GraphMember } from '@/contexts/FamilyGraphContext';

// ─── PUBLIC TYPES ─────────────────────────────────────────────────────────

/**
 * Every relationship category supported by the FamilyUnit overlay.
 * - `married`           — formal marriage, currently in effect.
 * - `partner`           — unmarried committed partnership / domestic partner.
 * - `fiance`            — engaged but not yet married.
 * - `coparent`          — share at least one child but not romantically
 *                         partnered (separated parents, donor co-parents).
 * - `divorced`          — formerly married, marriage ended.
 * - `ex_spouse`         — alias for divorced when used historically.
 * - `deceased_spouse`   — formerly married, partner is deceased.
 * - `step_parent`       — parent's current/former spouse who is not the
 *                         biological/adoptive parent of the child.
 * - `in_law`            — the spouse of any blood relative (son-in-law,
 *                         daughter-in-law, sister-/brother-in-law, etc.).
 *                         Stored on the spouse's row, not on the blood
 *                         relative's row. Renders BESIDE the blood relative.
 * - `blended`           — placeholder for multi-couple households (e.g.
 *                         polyamorous triads, multi-generational blended
 *                         family unit with > 2 adults).
 */
export type FamilyUnitType =
  | 'married'
  | 'partner'
  | 'fiance'
  | 'coparent'
  | 'divorced'
  | 'ex_spouse'
  | 'deceased_spouse'
  | 'step_parent'
  | 'in_law'
  | 'blended';

/**
 * Normalized representation of one row in `spouse_partner_edges`.
 * The Supabase column names use snake_case; this is the camelCase shape
 * used everywhere in the React app.
 */
export interface SpousePartnerEdge {
  id: string;
  ownerUserId: string | null;
  personAId: string;            // canonical-order: LEAST(person1_id, person2_id)
  personBId: string;            // canonical-order: GREATEST(person1_id, person2_id)
  unitType: FamilyUnitType;
  relationshipStatus: string | null; // raw status text, kept as-is for UI hints
  startedAt: string | null;     // ISO date or null
  endedAt: string | null;       // ISO date or null (set when divorced/widowed)
  isCurrent: boolean;
  metadata: Record<string, unknown>;
  notes: string | null;
  createdAt: string;
}

/**
 * A FamilyUnit is the in-memory overlay record the renderer reasons about.
 * Not all FamilyUnits are persisted in `family_units` — most are synthesized
 * on the fly from spouse_id + spouse_partner_edges. The persisted shape is
 * available in future iterations when a household-organizer wants to give
 * a unit a custom name ("The Lewis-Graham Household") or attach it to a
 * specific household record.
 */
export interface FamilyUnit {
  id: string;
  ownerUserId: string | null;
  unitType: FamilyUnitType;
  memberIds: string[]; // typically 2; left open for blended units
  householdId: string | null;
  startedAt: string | null;
  endedAt: string | null;
  isCurrent: boolean;
  source: 'spouse_id' | 'spouse_partner_edges' | 'family_units';
  metadata: Record<string, unknown>;
  notes: string | null;
}

export interface Household {
  id: string;
  ownerUserId: string;
  name: string;
  householdType: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface RelationshipHistoryEntry {
  id: string;
  ownerUserId: string;
  personAId: string;
  personBId: string;
  relationshipKind: string;
  status: string | null;
  startedAt: string | null;
  endedAt: string | null;
  metadata: Record<string, unknown>;
  notes: string | null;
  createdAt: string;
}

// ─── INTERNAL HELPERS ─────────────────────────────────────────────────────

/** Normalize a (a, b) member pair so (a, b) and (b, a) have the same key. */
const pairKey = (a: string, b: string): string =>
  a < b ? `${a}::${b}` : `${b}::${a}`;

/** Coerce an unknown unit_type string from the DB to a valid FamilyUnitType. */
const coerceUnitType = (raw: unknown): FamilyUnitType => {
  if (typeof raw !== 'string') return 'married';
  const v = raw.toLowerCase().trim();
  switch (v) {
    case 'married':
    case 'partner':
    case 'fiance':
    case 'coparent':
    case 'divorced':
    case 'ex_spouse':
    case 'deceased_spouse':
    case 'step_parent':
    case 'in_law':
    case 'blended':
      return v as FamilyUnitType;
    case 'fiancee':
    case 'fiancée':
    case 'engaged':
      return 'fiance';
    case 'spouse':
    case 'husband':
    case 'wife':
      return 'married';
    case 'ex':
    case 'ex-spouse':
    case 'exspouse':
      return 'ex_spouse';
    case 'co-parent':
    case 'co_parent':
      return 'coparent';
    case 'step-parent':
    case 'stepparent':
      return 'step_parent';
    case 'in-law':
    case 'inlaw':
      return 'in_law';
    default:
      return 'married';
  }
};

// ─── DATA LOADERS ─────────────────────────────────────────────────────────

/**
 * Load every spouse_partner_edges row for a given owner. The query is RLS-
 * scoped server-side anyway; the explicit owner filter is just an optimization.
 *
 * Resilient to:
 *   - The table not existing (older environments). Returns [].
 *   - The new columns (unit_type / started_at / etc.) not existing yet on
 *     a partially-migrated DB. Falls back to defaults for missing fields.
 *
 * Returns rows already normalized into the canonical (personAId < personBId)
 * order so callers can de-duplicate on `pairKey` without re-sorting.
 */
export const loadSpousePartnerEdges = async (
  supabase: SupabaseClient,
  ownerUserId?: string | null,
): Promise<SpousePartnerEdge[]> => {
  try {
    let q = supabase.from('spouse_partner_edges').select('*');
    if (ownerUserId) {
      // owner_user_id may be NULL on legacy rows — match both. We do this
      // by issuing two queries and merging (Supabase doesn't accept OR on
      // null comparisons cleanly with .or()).
      // The simpler path: just pull everything visible to RLS and let the
      // client filter. spouse_partner_edges is small (one row per couple).
    }
    const { data, error } = await q;
    if (error) {
      // Table missing or schema mismatch — silent fallback.
      console.debug('[familyUnits] loadSpousePartnerEdges fallback:', error.message);
      return [];
    }
    const rows = (data ?? []) as Array<Record<string, unknown>>;
    return rows.map((r): SpousePartnerEdge => {
      const p1 = String(r.person1_id ?? '');
      const p2 = String(r.person2_id ?? '');
      const [a, b] = p1 < p2 ? [p1, p2] : [p2, p1];
      return {
        id: String(r.id ?? ''),
        ownerUserId: (r.owner_user_id as string | null) ?? null,
        personAId: a,
        personBId: b,
        unitType: coerceUnitType(r.unit_type),
        relationshipStatus:
          (r.relationship_status as string | null) ?? null,
        startedAt: (r.started_at as string | null) ?? null,
        endedAt: (r.ended_at as string | null) ?? null,
        isCurrent:
          typeof r.is_current === 'boolean' ? r.is_current : true,
        metadata:
          (r.metadata as Record<string, unknown> | null) ?? {},
        notes: (r.notes as string | null) ?? null,
        createdAt:
          (r.created_at as string | null) ?? new Date().toISOString(),
      };
    });
  } catch (err) {
    console.debug('[familyUnits] loadSpousePartnerEdges threw:', err);
    return [];
  }
};

/** Best-effort loader for households. Returns [] when table is absent. */
export const loadHouseholds = async (
  supabase: SupabaseClient,
  ownerUserId: string,
): Promise<Household[]> => {
  try {
    const { data, error } = await supabase
      .from('households')
      .select('*')
      .eq('owner_user_id', ownerUserId);
    if (error) return [];
    return (data ?? []).map((r): Household => ({
      id: String((r as any).id),
      ownerUserId: String((r as any).owner_user_id),
      name: String((r as any).name ?? ''),
      householdType: String((r as any).household_type ?? 'family'),
      metadata: ((r as any).metadata ?? {}) as Record<string, unknown>,
      createdAt: String((r as any).created_at ?? ''),
    }));
  } catch {
    return [];
  }
};

/** Best-effort loader for relationship_history. Returns [] when table absent. */
export const loadRelationshipHistory = async (
  supabase: SupabaseClient,
  ownerUserId: string,
): Promise<RelationshipHistoryEntry[]> => {
  try {
    const { data, error } = await supabase
      .from('relationship_history')
      .select('*')
      .eq('owner_user_id', ownerUserId);
    if (error) return [];
    return (data ?? []).map((r): RelationshipHistoryEntry => ({
      id: String((r as any).id),
      ownerUserId: String((r as any).owner_user_id),
      personAId: String((r as any).person_a_id),
      personBId: String((r as any).person_b_id),
      relationshipKind: String((r as any).relationship_kind),
      status: ((r as any).status as string | null) ?? null,
      startedAt: ((r as any).started_at as string | null) ?? null,
      endedAt: ((r as any).ended_at as string | null) ?? null,
      metadata: ((r as any).metadata ?? {}) as Record<string, unknown>,
      notes: ((r as any).notes as string | null) ?? null,
      createdAt: String((r as any).created_at ?? ''),
    }));
  } catch {
    return [];
  }
};

// ─── PARTNER ENUMERATION (the placement-anchor primitive) ─────────────────

/**
 * Every distinct member id that `memberId` is partnered with through ANY
 * surface (spouse_id forward, reverse spouse_id, spouse_partner_edges).
 *
 * Returns ids only — callers can resolve them to GraphMember rows via the
 * `members` array. Rejected and self-id are filtered out.
 *
 * Cycle-safe: a member never appears as their own partner even if a
 * mis-saved row pointed `spouse_id` at themselves.
 */
export const enumeratePartnersOf = (
  memberId: string,
  members: GraphMember[],
  edges: SpousePartnerEdge[],
): string[] => {
  const out = new Set<string>();
  const me = members.find((m) => m.id === memberId);

  // 1. Forward spouse_id link.
  if (me?.spouseId && me.spouseId !== memberId) {
    const partner = members.find((m) => m.id === me.spouseId);
    if (partner && partner.approvalStatus !== 'rejected') {
      out.add(partner.id);
    }
  }

  // 2. Reverse spouse_id link — anyone whose spouseId points at us.
  for (const m of members) {
    if (m.id === memberId) continue;
    if (m.approvalStatus === 'rejected') continue;
    if (m.spouseId === memberId) out.add(m.id);
  }

  // 3. spouse_partner_edges — either side of the pair.
  for (const e of edges) {
    if (!e.isCurrent && e.unitType !== 'divorced' &&
        e.unitType !== 'ex_spouse' && e.unitType !== 'deceased_spouse') {
      // Past, non-historical edge — skip.
      continue;
    }
    if (e.personAId === memberId && e.personBId !== memberId) {
      const partner = members.find((m) => m.id === e.personBId);
      if (partner && partner.approvalStatus !== 'rejected') {
        out.add(partner.id);
      }
    } else if (e.personBId === memberId && e.personAId !== memberId) {
      const partner = members.find((m) => m.id === e.personAId);
      if (partner && partner.approvalStatus !== 'rejected') {
        out.add(partner.id);
      }
    }
  }

  return Array.from(out);
};

/**
 * Placement-anchor primitive — independent from ancestry proof.
 *
 * Returns true when `memberId` has at least ONE current or historical
 * partner edge (via any surface) to a member that the caller considers
 * "visible" (i.e. placeable on the tree). The caller passes the
 * visibility check as a callback so we don't depend on a specific
 * bloodline-engine module — this keeps the FamilyUnit engine DECOUPLED
 * from the bloodline engine.
 *
 * Cycle-guarded: the recursive walk through partner-of-partner chains
 * uses the `seen` set so a chain like A↔B↔A or A↔B↔C↔A cannot infinite-
 * loop. In practice, depth > 1 is only reached when `isVisible` itself
 * recursively delegates to this function — most call-sites pass a
 * pre-computed visibility set and depth stays at 1.
 */
export const isPartnerOfAnyVisibleMember = (
  memberId: string,
  members: GraphMember[],
  edges: SpousePartnerEdge[],
  isVisible: (id: string) => boolean,
  seen?: Set<string>,
): boolean => {
  const visited = seen ?? new Set<string>();
  if (visited.has(memberId)) return false;
  visited.add(memberId);

  const partners = enumeratePartnersOf(memberId, members, edges);
  for (const pid of partners) {
    if (visited.has(pid)) continue;
    if (isVisible(pid)) return true;
  }
  return false;
};

// ─── FAMILY UNIT SYNTHESIS ────────────────────────────────────────────────

/**
 * Synthesize the in-memory FamilyUnit list from the live graph state.
 *
 * Rules:
 *   - Every spouse_partner_edges row produces exactly ONE FamilyUnit
 *     (preferred source — has timeline metadata).
 *   - Every reciprocal spouse_id pair that is NOT already covered by a
 *     spouse_partner_edges row produces a default-married FamilyUnit.
 *     This is the legacy path; the operator can later promote it to a
 *     spouse_partner_edges row with explicit unit_type if they want to
 *     mark it divorced / partner / fiancé / etc.
 *   - One-sided spouse_id links (A.spouseId = B but B.spouseId !== A)
 *     are still surfaced, but with `isCurrent: false` and a note —
 *     the renderer can decide whether to draw a heart-line for them
 *     (currently it requires reciprocity in `pyramidLayout.buildCouples`).
 *   - Rejected members are skipped on both sides of every pair.
 */
export const buildFamilyUnits = (
  members: GraphMember[],
  edges: SpousePartnerEdge[],
): FamilyUnit[] => {
  const out: FamilyUnit[] = [];
  const seen = new Set<string>();
  const memberById = new Map<string, GraphMember>();
  for (const m of members) memberById.set(m.id, m);

  // 1. spouse_partner_edges first (preferred).
  for (const e of edges) {
    const a = memberById.get(e.personAId);
    const b = memberById.get(e.personBId);
    if (!a || !b) continue;
    if (a.approvalStatus === 'rejected' || b.approvalStatus === 'rejected') {
      continue;
    }
    const key = pairKey(a.id, b.id);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      id: e.id || `unit:${key}`,
      ownerUserId: e.ownerUserId,
      unitType: e.unitType,
      memberIds: [a.id, b.id],
      householdId: null,
      startedAt: e.startedAt,
      endedAt: e.endedAt,
      isCurrent: e.isCurrent,
      source: 'spouse_partner_edges',
      metadata: e.metadata,
      notes: e.notes,
    });
  }

  // 2. spouse_id pairs not already covered.
  for (const m of members) {
    if (m.approvalStatus === 'rejected') continue;
    if (!m.spouseId) continue;
    const partner = memberById.get(m.spouseId);
    if (!partner) continue;
    if (partner.approvalStatus === 'rejected') continue;
    if (partner.id === m.id) continue;
    const key = pairKey(m.id, partner.id);
    if (seen.has(key)) continue;
    seen.add(key);
    const reciprocal = partner.spouseId === m.id;
    out.push({
      id: `unit:${key}`,
      ownerUserId: m.ownerUserId ?? null,
      unitType: 'married',
      memberIds: [m.id, partner.id],
      householdId: null,
      startedAt: null,
      endedAt: null,
      isCurrent: reciprocal, // one-sided links flagged not-current
      source: 'spouse_id',
      metadata: reciprocal ? {} : { oneSided: true, anchor: m.id },
      notes: reciprocal
        ? null
        : 'spouse_id is one-sided — partner row needs reciprocation',
    });
  }

  return out;
};

/**
 * Find the FamilyUnit (if any) that a given member belongs to. When the
 * member belongs to multiple units (e.g. divorced from one partner,
 * remarried to another), returns the CURRENT one if present, otherwise
 * the most-recently-started historical one.
 */
export const findFamilyUnitForMember = (
  memberId: string,
  units: FamilyUnit[],
): FamilyUnit | null => {
  const involved = units.filter((u) => u.memberIds.includes(memberId));
  if (involved.length === 0) return null;
  const current = involved.find((u) => u.isCurrent);
  if (current) return current;
  return involved
    .slice()
    .sort((a, b) => (b.startedAt ?? '').localeCompare(a.startedAt ?? ''))[0];
};

/**
 * Human-readable label for a FamilyUnit type. Used by the GraphDebugPanel
 * and any future UI that lists family units (settings → my households).
 */
export const familyUnitTypeLabel = (t: FamilyUnitType): string => {
  switch (t) {
    case 'married':          return 'Married';
    case 'partner':          return 'Partner';
    case 'fiance':           return 'Fiancé(e)';
    case 'coparent':         return 'Co-parent';
    case 'divorced':         return 'Divorced';
    case 'ex_spouse':        return 'Ex-spouse';
    case 'deceased_spouse':  return 'Deceased spouse';
    case 'step_parent':      return 'Step-parent';
    case 'in_law':           return 'In-law';
    case 'blended':          return 'Blended household';
    default:                 return 'Family unit';
  }
};


// ─── getFamilyUnitAnchor — single source of truth for "is this person ──

//    connected via a spouse/partner/in-law edge to a member of the
//    visible tree?" The MANDATORY-RULE primitive defined verbatim in
//    the May 2026 "FINAL FORCE FIX" spec:
//
//      "If Person B is spouse/partner of Person A, and Person A is
//       visible on the tree, Person B is connected. Period.
//       No parent_ids required.
//       No bloodline proof required.
//       No ancestry placement required.
//       No Needs Connection allowed."
//
//    Every consumer that decides whether a member belongs in the
//    "Needs Connection" overflow row must call this helper FIRST.
//    If `isConnectedByFamilyUnit === true`, the member is excluded
//    from Needs Connection unconditionally — they are rendered in
//    the FamilyUnit overlay layer (chip beside the anchor) instead.
//
//    Pure read-only — never mutates members, parent_ids, spouse_id,
//    or any other field. Cycle-guarded against self-pointing rows.

export interface FamilyUnitAnchor {
  /** Member id of the visible bloodline anchor this partner attaches to. */
  anchorId: string;
  /** Display name of that anchor (for chip labels / debug). */
  anchorName: string;
  /**
   * Which surface produced the anchor link:
   *   - `spouse_id_forward`  member.spouse_id → anchor.id
   *   - `spouse_id_reverse`  anchor.spouse_id → member.id
   *   - `spouse_partner_edges` row covers the (member, anchor) pair
   */
  relationshipType:
    | 'spouse_id_forward'
    | 'spouse_id_reverse'
    | 'spouse_partner_edges';
  /**
   * Best-effort display label for the partner relative to the anchor.
   * Always one of: 'Spouse / Partner' (default), 'Ex-spouse',
   * 'Deceased spouse', 'Co-parent', 'Step-parent', 'Fiancé(e)',
   * 'In-law'. Caller may override with a viewer-perspective in-law
   * title (e.g. "Son-in-law to Marion") via the kinship engine.
   */
  displayLabel: string;
  /**
   * MANDATORY: when true, every Needs-Connection / overflow-row /
   * stranded-people consumer MUST exclude this member.
   */
  isConnectedByFamilyUnit: true;
  shouldExcludeFromNeedsConnection: true;
}

/**
 * Resolve the FamilyUnit anchor for a member, if any.
 *
 * @param member  The member we're asking about ("is Terrance connected?").
 * @param members The full live members array (the visible-or-not pool —
 *                the renderer decides visibility downstream).
 * @param edges   Optional spouse_partner_edges loaded from the DB. When
 *                omitted, only spouse_id forward + reverse links are
 *                considered. Either surface is sufficient on its own.
 * @param isVisible Optional callback that returns true when the given
 *                  candidate anchor is currently visible on the tree
 *                  (i.e. has a placed card, not in overflow). When
 *                  omitted, EVERY non-rejected non-self member is
 *                  considered a valid anchor — which is correct for
 *                  Needs-Connection gating because the spec states
 *                  "Person A is visible on the tree" is the precondition
 *                  for B's connection. Callers wiring this in to the
 *                  pyramid renderer pass a `placedSet.has(id)` predicate
 *                  so the gate matches actual on-canvas visibility.
 *
 * @returns The first valid anchor we find (deterministic order:
 *          spouse_partner_edges first, then spouse_id forward, then
 *          spouse_id reverse). null when no anchor is valid.
 */
export const getFamilyUnitAnchor = (
  member: GraphMember,
  members: GraphMember[],
  edges?: SpousePartnerEdge[],
  isVisible?: (id: string) => boolean,
): FamilyUnitAnchor | null => {
  if (!member) return null;
  if (member.approvalStatus === 'rejected') return null;
  const acceptCandidate = (cand: GraphMember | undefined | null): boolean => {
    if (!cand) return false;
    if (cand.id === member.id) return false;
    if (cand.approvalStatus === 'rejected') return false;
    if (isVisible && !isVisible(cand.id)) return false;
    return true;
  };
  const labelForType = (t: FamilyUnitType): string => {
    switch (t) {
      case 'divorced':
      case 'ex_spouse':
        return 'Ex-spouse';
      case 'deceased_spouse':
        return 'Deceased spouse';
      case 'coparent':
        return 'Co-parent';
      case 'step_parent':
        return 'Step-parent';
      case 'fiance':
        return 'Fiancé(e)';
      case 'in_law':
        return 'In-law';
      case 'blended':
        return 'Family unit';
      case 'married':
      case 'partner':
      default:
        return 'Spouse / Partner';
    }
  };

  // 1. spouse_partner_edges (preferred — has timeline metadata).
  if (edges && edges.length > 0) {
    for (const e of edges) {
      let candId: string | null = null;
      if (e.personAId === member.id) candId = e.personBId;
      else if (e.personBId === member.id) candId = e.personAId;
      if (!candId) continue;
      const cand = members.find((m) => m.id === candId);
      if (!acceptCandidate(cand)) continue;
      return {
        anchorId: cand!.id,
        anchorName: cand!.name,
        relationshipType: 'spouse_partner_edges',
        displayLabel: labelForType(e.unitType),
        isConnectedByFamilyUnit: true,
        shouldExcludeFromNeedsConnection: true,
      };
    }
  }

  // 2. Forward spouse_id — member.spouseId points at an anchor.
  if (member.spouseId && member.spouseId !== member.id) {
    const cand = members.find((m) => m.id === member.spouseId);
    if (acceptCandidate(cand)) {
      return {
        anchorId: cand!.id,
        anchorName: cand!.name,
        relationshipType: 'spouse_id_forward',
        displayLabel: 'Spouse / Partner',
        isConnectedByFamilyUnit: true,
        shouldExcludeFromNeedsConnection: true,
      };
    }
  }

  // 3. Reverse spouse_id — someone else's spouseId points at member.
  for (const cand of members) {
    if (cand.id === member.id) continue;
    if (cand.spouseId !== member.id) continue;
    if (!acceptCandidate(cand)) continue;
    return {
      anchorId: cand.id,
      anchorName: cand.name,
      relationshipType: 'spouse_id_reverse',
      displayLabel: 'Spouse / Partner',
      isConnectedByFamilyUnit: true,
      shouldExcludeFromNeedsConnection: true,
    };
  }

  return null;
};

/**
 * Convenience wrapper: just the boolean. When you only need the gate
 * answer ("should I drop this member from Needs Connection?"), this
 * is the one-liner. Equivalent to `!!getFamilyUnitAnchor(...)`.
 */
export const isConnectedByFamilyUnit = (
  member: GraphMember,
  members: GraphMember[],
  edges?: SpousePartnerEdge[],
  isVisible?: (id: string) => boolean,
): boolean => !!getFamilyUnitAnchor(member, members, edges, isVisible);
